import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const TestimonialsSection = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: var(--color-bg-light);
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(var(--color-accent-primary) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.05;
  pointer-events: none;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const SectionHeading = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-lg);
`;

const HeadingLabel = styled(motion.div)`
  color: var(--color-accent-primary);
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: var(--spacing-sm);
`;

const SectionTitle = styled(motion.h2)`
  margin-bottom: var(--spacing-md);
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(
      90deg,
      var(--color-accent-primary) 0%,
      var(--color-accent-secondary) 100%
    );
    border-radius: var(--radius-full);
  }
`;

const SliderContainer = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  
  .slide-indicators {
    display: flex;
    justify-content: center;
    margin-top: var(--spacing-md);
    gap: 0.5rem;
  }
  
  .slider-controls {
    margin-top: var(--spacing-md);
    display: flex;
    justify-content: center;
    gap: 1rem;
  }
`;

const SliderButton = styled(motion.button)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--color-accent-primary);
    color: var(--color-bg-dark);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SlideIndicator = styled(motion.button)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$active ? 'var(--color-accent-primary)' : 'rgba(255, 255, 255, 0.2)'};
  border: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--color-accent-primary)' : 'rgba(255, 255, 255, 0.4)'};
  }
`;

const TestimonialSlide = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TestimonialCard = styled(motion.div)`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '"';
    position: absolute;
    top: -20px;
    left: 20px;
    font-size: 150px;
    font-family: var(--font-heading);
    color: rgba(255, 255, 255, 0.03);
    z-index: -1;
  }
  
  &::after {
    content: '"';
    position: absolute;
    bottom: -100px;
    right: 20px;
    font-size: 150px;
    font-family: var(--font-heading);
    color: rgba(255, 255, 255, 0.03);
    z-index: -1;
  }
`;

const TestimonialContent = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  color: var(--color-text-primary);
  font-style: italic;
  margin-bottom: var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: var(--spacing-md);
`;

const AuthorName = styled.h4`
  font-size: 1.125rem;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.p`
  color: var(--color-accent-primary);
  font-size: 0.875rem;
  margin-bottom: 0;
`;

const StarRating = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-md);
  
  svg {
    color: var(--color-accent-secondary);
    margin: 0 2px;
  }
`;

const testimonialData = [
  {
    id: 1,
    content: "Working with Laura transformed how our district approaches student support. Her assessments are thorough yet practical, and her recommendations have made a measurable difference in student outcomes across our classrooms.",
    author: "School Administrator",
    role: "Mountain View District",
    rating: 5
  },
  {
    id: 2,
    content: "The strategies Laura provided were game-changers for my classroom. For the first time, I feel equipped to support students with diverse learning needs while maintaining a positive learning environment for everyone. Her approach is innovative yet practical.",
    author: "Elementary School Teacher",
    role: "Denver Public Schools",
    rating: 5
  },
  {
    id: 3,
    content: "Laura helped us navigate a challenging time with compassion and expertise. She translated complex educational processes into clear steps and empowered us to effectively advocate for our child. We're forever grateful for her guidance.",
    author: "Parent",
    role: "Colorado Springs",
    rating: 5
  }
];

const TestimonialSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for right, -1 for left
  const slideInterval = useRef(null);
  
  const startSlideTimer = useCallback(() => {
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
    }
    
    slideInterval.current = setInterval(() => {
      setDirection(1);
      setCurrentSlide((prev) => (prev === testimonialData.length - 1 ? 0 : prev + 1));
    }, 6000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    // Auto-advance slides
    startSlideTimer();
    
    return () => {
      if (slideInterval.current) {
        clearInterval(slideInterval.current);
      }
    };
  }, [currentSlide, startSlideTimer]);
  
  const nextSlide = () => {
    setDirection(1);
    setCurrentSlide((prev) => (prev === testimonialData.length - 1 ? 0 : prev + 1));
  };
  
  const prevSlide = () => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev === 0 ? testimonialData.length - 1 : prev - 1));
  };
  
  const goToSlide = (index) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  };
  
  // Animation variants
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -1000 : 1000,
      opacity: 0,
    }),
  };
  
  const testimonial = testimonialData[currentSlide];
  
  return (
    <TestimonialsSection>
      <BackgroundPattern />
      
      <ContentContainer>
        <SectionHeading>
          <HeadingLabel
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: false, amount: 0.2 }}
          >
            What Others Say
          </HeadingLabel>
          
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: false, amount: 0.2 }}
          >
            Client Testimonials
          </SectionTitle>
        </SectionHeading>
        
        <SliderContainer>
          <div style={{ position: 'relative', height: '300px' }}>
            <AnimatePresence custom={direction} initial={false}>
              <TestimonialSlide
                key={currentSlide}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
              >
                <TestimonialCard>
                  <StarRating>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                      </svg>
                    ))}
                  </StarRating>
                  
                  <TestimonialContent>"{testimonial.content}"</TestimonialContent>
                  
                  <TestimonialAuthor>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorRole>{testimonial.role}</AuthorRole>
                  </TestimonialAuthor>
                </TestimonialCard>
              </TestimonialSlide>
            </AnimatePresence>
          </div>
          
          <div className="slider-controls">
            <SliderButton
              onClick={prevSlide}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SliderButton>
            
            <SliderButton
              onClick={nextSlide}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SliderButton>
          </div>
          
          <div className="slide-indicators">
            {testimonialData.map((_, index) => (
              <SlideIndicator
                key={index}
                $active={currentSlide === index}
                onClick={() => goToSlide(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </SliderContainer>
      </ContentContainer>
    </TestimonialsSection>
  );
};

export default TestimonialSlider;